<template>
  <SoDialog
    ref="dialog"
    v-model="open"
    :max-width="1500"
    :loading="mandrillEmails.isFetching"
    card-title="Search Email Delivery Status"
  >
    <SoDataTable
      :headers="headers"
      :items="tableItems"
      :searchable="false"
      :loading="mandrillEmails.isFetching"
      show-copy-to-clipboard
      @click:row="() => {}"
    >
      <template #top>
        <div class="mandrill-modal__filters d-flex">
          <SoSelect
            v-model="selectedEmail"
            :items="emails"
            placeholder="Email"
          />
          <SoDatePicker v-model="dateRange" type="daterange" />
          <SoButton title="Submit" large @click="getMandrillEmails()" />
          <SoButton title="Reset" large @click="reset()" />
        </div>
      </template>
    </SoDataTable>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"

  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import dayjs from "dayjs"
  import {
    SoDataTableColumnType,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import { MandrillService } from "@/services/MandrillService"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import { QueryResult } from "@/api/utils/types"
  import { MandrillEmail } from "@/models/mandrillEmail/mandrillEmail"
  import { SoChipType } from "@/types/components/SoChip"
  import { useCurrentAccountStore } from "@/stores/currentAccount"

  export default defineComponent({
    components: {
      SoDataTable,
      SoDialog,
      SoDatePicker,
      SoButton,
      SoSelect,
    },
    props: {
      email: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        open: false,
        selectedEmail: "",
        current: useCurrentAccountStore(),
        mandrillEmails: {} as QueryResult<MandrillEmail[]>,
        dateRange: [this.subtractSixMonths(new Date()), new Date()],
        headers: [
          {
            key: "emailSentDate",
            title: "Date",
            type: SoDataTableColumnType.DATE,
          },
          { key: "status", title: "Status", type: SoDataTableColumnType.CHIP },
          { key: "subject", title: "Subject" },
          { key: "sender", title: "Sent By" },
          { key: "clicks", title: "Clicks" },
          { key: "opens", title: "Opens" },
          { key: "bounceDetail", title: "Rejects" },
        ],
      }
    },
    computed: {
      tableItems(): SoDataTableItem[] {
        if (!this.mandrillEmails.data) return []
        return this.mandrillEmails.data.map((item) => {
          return {
            emailSentDate: item.emailSentDate,
            // Null status leaves the cell on the table's empty fallback, rather
            // than rendering a chip with no text.
            status: item.status
              ? {
                  value: item.status,
                  props: {
                    text: item.status,
                    type: this.getStatusChipType(item.status),
                  },
                }
              : null,
            subject: item.subject,
            sender: item.sender,
            clicks: item.clicks,
            opens: item.opens,
            bounceDetail: item.bounceDetail || "",
          }
        })
      },
      emails() {
        return this.current.accountContacts.data.map((contact) => contact.email)
      },
    },
    watch: {
      email() {
        this.$nextTick(() => {
          this.reset()
          this.getMandrillEmails()
        })
      },
    },
    created() {
      if (this.email) {
        this.getMandrillEmails()
      }
    },
    methods: {
      subtractSixMonths(date: Date): Date {
        date.setMonth(date.getMonth() - 6)
        return date
      },
      reset() {
        this.selectedEmail = this.email
        this.dateRange = [this.subtractSixMonths(new Date()), new Date()]
      },
      getMandrillEmails() {
        this.mandrillEmails = MandrillService.getMandrillEmailsList(
          "full_email:" + this.selectedEmail,
          dayjs(this.dateRange[0]),
          dayjs(this.dateRange[1]),
        )
      },
      getStatusChipType(status: string): string {
        return status === "sent" ? SoChipType.SUCCESS : SoChipType.ERROR
      },
    },
  })
</script>

<style scoped>
  .mandrill-modal__text-field {
    width: 16rem;
  }

  .mandrill-modal__filters {
    gap: 0.5rem;
  }
</style>
